import React from 'react';

import { TileGridTile } from 'api/cms';
import { Icon } from 'components/Icon';
import { FilestackImage } from 'components/Image';
import { Body } from 'components/Text';
import { useHideDocumentScroll } from 'utils/hooks';

import {
  TileGridBlockTileContent,
  TileGridBlockTileContentImageContainer,
  TileGridBlockTileContentLeft,
  TileGridBlockTileContentRight,
  TileGridBlockTileHeading,
  TileGridBlockTileModalActionsContainer,
  TileGridBlockTileModalArrow,
  TileGridBlockTileModalArrowsContainer,
  TileGridBlockTileModalBackdrop,
  TileGridBlockTileModalCloseButton,
  TileGridBlockTileModalCloseButtonText,
  TileGridBlockTileModalContainer,
  TileGridBlockTileSubheading,
  TileGridBlockTileText,
} from './TileGridBlockTileModal.styled';

type Props = {
  tile: TileGridTile;
  blockIndex: number;
  onClose: () => void;
  onPrevious: (() => void) | undefined;
  onNext: (() => void) | undefined;
};

export function TileGridBlockTileModal({
  tile,
  blockIndex,
  onClose,
  onPrevious,
  onNext,
}: Props) {
  const { image, heading, subHeading, description } = tile;

  useHideDocumentScroll();

  return (
    <>
      <TileGridBlockTileModalBackdrop role="button" onClick={onClose} />

      <TileGridBlockTileModalContainer>
        <TileGridBlockTileModalActionsContainer>
          <TileGridBlockTileModalArrowsContainer>
            <TileGridBlockTileModalArrow
              type="button"
              onClick={onPrevious}
              disabled={!onPrevious}
            >
              <Icon
                name="chevron-left"
                size={20}
                initialColorName="white"
                keepColorOnLoad
              />
            </TileGridBlockTileModalArrow>

            <TileGridBlockTileModalArrow
              type="button"
              onClick={onNext}
              disabled={!onNext}
            >
              <Icon
                name="chevron-right"
                size={20}
                initialColorName="white"
                keepColorOnLoad
              />
            </TileGridBlockTileModalArrow>
          </TileGridBlockTileModalArrowsContainer>

          <TileGridBlockTileModalCloseButton
            type="button"
            data-qa-id="cms-tg-close-button"
            onClick={onClose}
          >
            <TileGridBlockTileModalCloseButtonText>
              {getText('close')}
            </TileGridBlockTileModalCloseButtonText>

            <Icon initialColorName="battleshipGrey" name="close" size={24} />
          </TileGridBlockTileModalCloseButton>
        </TileGridBlockTileModalActionsContainer>

        <TileGridBlockTileContent>
          <TileGridBlockTileContentLeft>
            <TileGridBlockTileContentImageContainer
              style={{ maxWidth: image.width, maxHeight: image.height }}
            >
              <FilestackImage data={image} objectFit="cover" expandSize />
            </TileGridBlockTileContentImageContainer>
          </TileGridBlockTileContentLeft>

          <TileGridBlockTileContentRight>
            <TileGridBlockTileText>
              <TileGridBlockTileHeading>{heading}</TileGridBlockTileHeading>

              <TileGridBlockTileSubheading>
                {subHeading}
              </TileGridBlockTileSubheading>

              <Body
                analyticsData={{
                  eventName: 'CMS Block',
                  eventProperties: {
                    block_position: blockIndex,
                    block_type: 'Tile Grid',
                  },
                }}
                dangerouslySetInnerHTML={{
                  __html: description,
                }}
                styleAs={description.length > 360 ? 'bodySmall' : 'body'}
              />
            </TileGridBlockTileText>
          </TileGridBlockTileContentRight>
        </TileGridBlockTileContent>
      </TileGridBlockTileModalContainer>
    </>
  );
}
